import Box from '@mui/material/Box';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useSnackbar } from 'notistack';
import AlertMessages from 'common/Alert/AlertMessages';
import { useTranslation } from 'react-i18next';
import { checkPrivileges } from 'Helpers/CheckPrivileges';
import BackendPaginationTable from 'common/Table/TableWithPagination';
import { useNavigate, useParams } from 'react-router-dom';
import ComponentHeaderTwo from 'common/ComponentHeaderTwo/ComponentHeaderTwo';
import { ReactComponent as Usergroup } from "assets/SideBarIcons/user-group.svg";
import { useGetUserGroupUserService, useAssignUserService, useUnassignUserService, useAssignAllUserService, useUnassignAllUserService } from 'subModule/src/services/usergroup/useUserGroup';
import UserGroupTabs from './UserGroupTabs';
import UserGroupUserSearchHeader from 'components/UserGroup/UserGroupUserSearchHeader';
import { SelectChangeEvent } from '@mui/material';
import { selectUserInfo } from 'features/user/userSlice';
import { useSelector } from 'react-redux';
import { UserGroupProfileService } from 'subModule/src/core/services';
import type { BreadcrumbItem } from 'Helpers/generalBreadcrumbs';

interface SearchFormData {
    [key: string]: string | any;
}
export default function UserGroupUserTable() {
    const params = useParams();
    const groupId = params?.id;
    const navigate = useNavigate();
    const [pageNo, setPageNo] = useState<number>(1);
    const [pageSize, setPageSize] = useState<number>(20);
    const [loading, setLoading] = useState<boolean>(false);
    const [filter, setFilter] = useState<string[]>(['name']);
    const [search, setSearch] = useState<any>({});
    const [searchFormData, setSearchFormData] = useState<SearchFormData>({ name: '', userName: '', type: 0 });
    const [groupTitle, setGroupTitle] = useState('');
    const { data: tableData, isFetching, refetch } = useGetUserGroupUserService({ pageNumber: pageNo, size: pageSize, ...search }, +(params as any).id);
    const { enqueueSnackbar } = useSnackbar();
    const { t } = useTranslation();
    const loggedInUserData = useSelector(selectUserInfo);
    const loggedInUserType = loggedInUserData.type;
    const userTypesData = [
        { value: 100, label: t('Reseller') },
        { value: 110, label: t('Maintenance Head') },
        { value: 120, label: t('Service Provider') },
        { value: 130, label: t('Station Manager') },
        { value: 140, label: t('Service Manager') },
        { value: 150, label: t('Technician') },
        { value: 200, label: t('Supervisor') },
        { value: 210, label: t('Inventory Manager') },
        { value: 220, label: t('Account Manager') },
    ];
    const userTypesOption = 
        loggedInUserType === -1 ?
            userTypesData.filter(option => option.value === 100 || option.value === 110)
        : loggedInUserType === 100 ?
            userTypesData.filter(option => option.value === 110)
        : loggedInUserType === 110 ?
            userTypesData.filter(option => option.value > loggedInUserType)
        : loggedInUserType === 120 ? 
            userTypesData.filter(option => [130, 140, 150].includes(option.value))
        : loggedInUserType === 130 ? 
            userTypesData.filter(option => [140, 150].includes(option.value))
        : loggedInUserType === 140 ? 
            userTypesData.filter(option => [150].includes(option.value))
        : [];
    let allUsersAssigned: unknown = tableData?.pages[0].data?.content.map(item => item.linked).every(linked => linked === true);

    useEffect(() => {
        if (!groupId) return;
        let active = true;
        UserGroupProfileService.getUserGroupByID(+groupId)
            .then((res: any) => {
                if (!active) return;
                const name = String(
                    res?.data?.title ||
                        res?.data?.secondaryTitle ||
                        res?.title ||
                        '',
                ).trim();
                if (name) setGroupTitle(name);
            })
            .catch(() => {});
        return () => {
            active = false;
        };
    }, [groupId]);

    const breadcrumbItems: BreadcrumbItem[] = useMemo(
        () => [
            { labelKey: 'general' },
            { labelKey: 'usersGroup', href: '/user-groups' },
            {
                labelKey: 'title',
                ...(groupTitle ? { label: groupTitle } : {}),
                href: groupId ? `/user-group/${groupId}` : undefined,
            },
            { labelKey: 'assignUser' },
        ],
        [groupId, groupTitle],
    );

    const fetchData = useCallback(() => {
        refetch().then((res: any) => {
            if (!res?.data?.pages[0]?.success) enqueueSnackbar(<AlertMessages text={res?.data?.pages[0]?.message || "Something went wrong"} />, { variant: "error" });
        });
    }, []);
    useEffect(() => {
        fetchData();
    }, [pageNo, pageSize, search]);
    const handleChangePage = (pageNo: number) => {
        setPageNo(pageNo + 1);
    }
    const handleChangeRows = (pageSize: number) => {
        setPageSize(pageSize);
        setPageNo(1);
    }
    const clearSearch = () => {
        setFilter(['name']);
        setSearchFormData({ name: '', userName: '', type: 0 });
        if (Object.keys(search).length) {
            setSearch({});
        }
    }
    const handleChangeFilter = (event: SelectChangeEvent<typeof filter>) => {
        const { target: { value }, } = event;
        setFilter(typeof value === 'string' ? value.split(',') : value,);
        setSearchFormData(prevFormData =>
            Object.fromEntries(Object.keys(prevFormData).map(key =>
                [key, value.includes(key) ? prevFormData[key] : (typeof prevFormData[key] === 'string' ? '' : 0)]
            ))
        );
        if (!value.length && Object.keys(search).length) {
            clearSearch();
        }
    };
    const handleChangeSearch = (field: string, value: any) => {
        const val = field === 'type' ? (value ? parseInt(value.value) || 0 : 0) : value;
        setSearchFormData((prevData) => ({
            ...prevData,
            [field]: val,
        }));
    };
    const handleSubmitSearch = () => {
        const filteredSearch: Record<string, string> = {};
        Object.entries(searchFormData).forEach(([key, value]) => {
            if (value) filteredSearch[key] = typeof value === 'string' ? value?.trim() : value;
        });
        if(Object.keys(filteredSearch).length > 0 && Object.values(filteredSearch).some(value => value)) {
            setSearch(filteredSearch);
            setPageNo(1);
            setPageSize(pageSize);
        }
    };
    const assignUnassignSingleEntity = (isChecked: boolean, row: any) => {
        setLoading(true);
        if (isChecked) {
            onAssignUser({ userGroupId: +(params as any).id, userId: row.id });
        } else {
            onUnassignUser({ userGroupId: +(params as any).id, userId: row.id });
        }
    }
    const assignUnassignAllEntities = (isChecked: boolean) => {
        setLoading(true);
        if (isChecked) {
            onAssignAllUsers({ userGroupId: +(params as any).id });
        } else {
            onUnassignAllUsers({ userGroupId: +(params as any).id });
        }
    }
    const { onAssignUser } = useAssignUserService({
        onSuccess: (msg) => {
            const response: any = {};
            Object.keys(msg).forEach((key, index) => { response[key] = Object.values(msg)[index]; });
            if (response.success) {
                if (tableData?.pages[0].data.content.length === tableData?.pages[0].data.content.filter(user => user.linked).length) {
                    allUsersAssigned = true;
                }
                refetch();
                setLoading(false);
                enqueueSnackbar(<AlertMessages statusCode={response.status} text={t('user') + ' ' + t('assigned') + ' ' + t('successfully')} />, { variant: 'success' });
            } else {
                refetch();
                setLoading(false);
                enqueueSnackbar(<AlertMessages statusCode={response.status} />, { variant: 'error' });
            }
        },
        onError: (err: any) => {
            enqueueSnackbar(<AlertMessages statusCode={0} />, { variant: 'error' });
            if (err instanceof Error) {
                console.error(err.stack);
            }
        }
    })
    const { onUnassignUser } = useUnassignUserService({
        onSuccess: (data) => {
            const response: any = {};
            Object.keys(data).forEach((key, index) => { response[key] = Object.values(data)[index]; });
            if (response.success) {
                if (tableData?.pages[0].data.content.length !== tableData?.pages[0].data.content.filter(user => user.linked).length) {
                    allUsersAssigned = false;
                }
                refetch();
                setLoading(false);
                enqueueSnackbar(<AlertMessages statusCode={response.status} text={t('user') + ' ' + t('unassigned') + ' ' + t('successfully')} />, { variant: 'success' });
            } else {
                refetch();
                setLoading(false);
                enqueueSnackbar(<AlertMessages statusCode={response.status} />, { variant: 'error' });
            }
        },
        onError: (err: any) => {
            console.log("err====,  ", err);
            enqueueSnackbar(<AlertMessages statusCode={0} />, { variant: 'error' });
        },
    });
    const { onAssignAllUsers } = useAssignAllUserService({
        onSuccess: (msg) => {
            const response: any = {};
            Object.keys(msg).forEach((key, index) => { response[key] = Object.values(msg)[index]; });
            if (response.success) {
                allUsersAssigned = true;
                refetch();
                setLoading(false);
                enqueueSnackbar(<AlertMessages statusCode={response.status} text={t('All') + ' ' + t('users') + ' ' + t('assigned') + ' ' + t('successfully')} />, { variant: 'success' });
            } else {
                refetch();
                setLoading(false);
                enqueueSnackbar(<AlertMessages statusCode={response.status} />, { variant: 'error' });
            }
        },
        onError: (err: any) => {
            enqueueSnackbar(<AlertMessages statusCode={0} />, { variant: 'error' });
            if (err instanceof Error) {
                console.error(err.stack);
            }
        }
    })
    const { onUnassignAllUsers } = useUnassignAllUserService({
        onSuccess: (data) => {
            const response: any = {};
            Object.keys(data).forEach((key, index) => { response[key] = Object.values(data)[index]; });
            if (response.success) {
                allUsersAssigned = false;
                refetch();
                setLoading(false);
                enqueueSnackbar(<AlertMessages statusCode={response.status} text={t('All') + ' ' + t('users') + ' ' + t('unassigned') + ' ' + t('successfully')} />, { variant: 'success' });
            } else {
                refetch();
                setLoading(false);
                enqueueSnackbar(<AlertMessages statusCode={response.status} />, { variant: 'error' });
            }
        },
        onError: (err: any) => {
            console.log("err====,  ", err);
            enqueueSnackbar(<AlertMessages statusCode={0} />, { variant: 'error' });
        },
    });

    return (
        <Box>
            <ComponentHeaderTwo
                showBtn={true}
                showSaveBtn={false}
                onClick={() => navigate('/user-groups')}
                heading={t('usersGroup')}
                icon={<Usergroup height={28} width={28} />}
                btnText={"back"}
                btnType={"back"}
                showCreateBtn={false}
                breadcrumbItems={breadcrumbItems}
            />
            <Box>
                <div className='flex'>
                    {params?.id ? <UserGroupTabs params={params} selectedIndex={1} /> : null}
                    <BackendPaginationTable
                        columns={[
                            { id: 'name', label: 'name', numeric: false },
                            { id: 'username', label: 'email', numeric: false },
                            { id: 'type', label: 'type', numeric: false },
                        ]}
                        data={tableData?.pages[0]?.data?.content || []}
                        pageNo={pageNo}
                        pageSize={pageSize}
                        tableData={tableData?.pages[0]?.data}
                        showCheckbox={true}
                        showActions={true}
                        enableSorting={true}
                        showHeader={true}
                        showSearch={true}
                        showFilter={true}
                        showCreateButton={false}
                        actions={[]}
                        handleChangePage={handleChangePage}
                        handleChangeRows={handleChangeRows}
                        assignUnassignSingleEntity={assignUnassignSingleEntity}
                        assignUnassignAllEntities={assignUnassignAllEntities}
                        allEntitiesAssigned={allUsersAssigned}
                        loadingSearch={isFetching || loading}
                        assignPrivilege={checkPrivileges('ROLE_USERGROUP_USER_ASSIGN')}
                        unassignPrivilege={checkPrivileges('ROLE_USERGROUP_USER_UNASSIGN')}
                        headerComponent={<UserGroupUserSearchHeader 
                            searchFormData={searchFormData}
                            handleChangeFilter={handleChangeFilter}
                            handleChangeSearch={handleChangeSearch}
                            handleSubmitSearch={handleSubmitSearch}
                            clearSearch={clearSearch}
                            search={search}
                            {...{filter, userTypesOption}}
                        />}
                    />
                </div>
            </Box>
        </Box>
    )
}